import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import QuickHandoverModal from './QuickHandoverModal';
import HandoverReminderOverlay from './HandoverReminderOverlay';
import { useHandoverStore } from '../store/handoverStore';
import ModuleAccess from './ModuleAccess';
import { usePermissions } from '../hooks/usePermissions';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { 
    bookingToHandover, 
    bookingIdToHandover, 
    setBookingToHandover, 
    setBookingIdToHandover 
  } = useHandoverStore();

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar open={sidebarOpen} onToggle={handleSidebarToggle} />
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: 0 }}>
        <TopBar onMenuToggle={handleSidebarToggle} sidebarOpen={sidebarOpen} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
          }}
        >
          <ModuleAccess>{children}</ModuleAccess>
        </Box>
      </Box>

      {/* Global Quick Handover Modal */}
      {hasPermission('pos.handover') && <QuickHandoverModal
        open={!!bookingToHandover || !!bookingIdToHandover}
        booking={bookingToHandover}
        bookingId={bookingIdToHandover}
        onClose={() => {
          setBookingToHandover(null);
          setBookingIdToHandover(null);
        }}
        onSuccess={() => {
          setBookingToHandover(null);
          setBookingIdToHandover(null);
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['vehicles'] });
          queryClient.invalidateQueries({ queryKey: ['vehicle-stats'] });
          queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
        }}
      />}

      {/* Persistent Handover Reminders */}
      {hasPermission('bookings.view') && hasPermission('pos.handover') && <HandoverReminderOverlay />}
    </Box>
  );
};

export default Layout;
